// Thin, typed access to the GTM dataLayer and to the bootstrap that
// public/index.html defines before any other script runs (window.emdrAnalytics).
// The app never talks to GA4 or any vendor directly: it only describes what
// happened, and GTM decides which tags use it. See ./README.md.

export type DataLayerValue = string | number | boolean | null | undefined;
export type DataLayerParams = Record<string, DataLayerValue>;

export interface EmdrAnalyticsBootstrap {
    gtmId: string;
    consentCookie: string;
    consentVersion: number;
    isExcludedScreen: boolean;
    pageLocationPinned: boolean;
    cleanUrl: (href: string) => string;
    loadDeferredGtm: () => boolean;
}

declare global {
    interface Window {
        dataLayer?: unknown[];
        emdrAnalytics?: EmdrAnalyticsBootstrap;
    }
}

export function getDataLayer(): unknown[] {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
}

export function pushToDataLayer(payload: Record<string, DataLayerValue>): void {
    getDataLayer().push(payload);
}

/** Push a business event. Never pass personal data, patient data or invite ids. */
export function trackEvent(event: string, params: DataLayerParams = {}): void {
    pushToDataLayer({...params, event});
}

/** gtag() command (e.g. consent update). GTM only accepts real `arguments` objects. */
export function gtag(..._args: unknown[]): void {
    // eslint-disable-next-line prefer-rest-params
    getDataLayer().push(arguments);
}

/** URL safe to hand to analytics: session invite ids and OAuth params removed. */
export function cleanUrl(href: string): string {
    return window.emdrAnalytics ? window.emdrAnalytics.cleanUrl(href) : "";
}

/** Patient screens load no GTM and show no consent banner. */
export function isExcludedScreen(): boolean {
    return window.emdrAnalytics ? window.emdrAnalytics.isExcludedScreen : false;
}

/** True when index.html pinned GA4's page_location because the URL carries an invite id. */
export function isPageLocationPinned(): boolean {
    return window.emdrAnalytics ? window.emdrAnalytics.pageLocationPinned : false;
}

/** Loads GTM if index.html deferred it for the Auth0 callback; otherwise a no-op. */
export function loadDeferredGtm(): boolean {
    return window.emdrAnalytics ? window.emdrAnalytics.loadDeferredGtm() : false;
}
